/**
 * FundElite i18n - Phase 9.1
 * 
 * Simple internationalization system.
 * Currently supports: English, Spanish
 * 
 * Fallback: English for untranslated strings
 */

export const translations = {
  en: {
    // Common
    'common.loading': 'Loading...',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.close': 'Close',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.reset': 'Reset',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.submit': 'Submit',
    'common.confirm': 'Confirm',
    'common.yes': 'Yes',
    'common.no': 'No',
    
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.agencies': 'Agencies',
    'nav.users': 'Users',
    'nav.activity': 'Activity',
    'nav.settings': 'Settings',
    'nav.signOut': 'Sign Out',
    'nav.quickSearch': 'Quick Search',
    
    // Settings
    'settings.title': 'Settings',
    'settings.profile': 'Profile',
    'settings.appearance': 'Appearance',
    'settings.preferences': 'Preferences',
    'settings.security': 'Security',
    'settings.advanced': 'Advanced',
    'settings.branding': 'Branding',
    
    // Appearance
    'appearance.title': 'Appearance',
    'appearance.subtitle': 'Customize how FundElite looks for you',
    'appearance.accentColor': 'Accent Color',
    'appearance.accentColorDesc': 'Choose your preferred accent color',
    'appearance.mode': 'Appearance Mode',
    'appearance.modeDesc': 'Select light, dark, or system preference',
    'appearance.system': 'System',
    'appearance.light': 'Light',
    'appearance.dark': 'Dark',
    'appearance.language': 'Language',
    'appearance.languageDesc': 'Choose your preferred language',
    'appearance.resetDefaults': 'Reset to Defaults',
    'appearance.resetDesc': 'Restore all appearance settings to default',
    
    // Accent presets
    'accent.fundelite_emerald': 'FundElite Emerald',
    'accent.deep_teal': 'Deep Teal',
    'accent.mint_teal': 'Mint Teal',
    'accent.dark_emerald': 'Dark Emerald',
    'accent.soft_green': 'Soft Green',
    'accent.slate_premium': 'Slate Premium',
    
    // Support Mode
    'support.mode': 'Support Mode',
    'support.viewingAs': 'Viewing as',
    'support.agency': 'Agency',
    'support.returnToPlatform': 'Return to Platform Owner',
    'support.readOnly': 'Read-Only',
    'support.interactive': 'Interactive',
    
    // Context
    'context.platformOwner': 'Platform Owner',
    'context.agencyAdmin': 'Agency Admin',
    'context.client': 'Client',
    'context.selfServe': 'Self-Serve',
    'context.managed': 'Managed',
    
    // Session Activity
    'session.title': 'Team Activity',
    'session.subtitle': 'Monitor team session activity',
    'session.userName': 'User Name',
    'session.email': 'Email',
    'session.ipAddress': 'IP Address',
    'session.loginTime': 'Login Time',
    'session.logoutTime': 'Logout Time',
    'session.status': 'Status',
    'session.active': 'Active',
    'session.ended': 'Ended',
    'session.noData': 'No session data available',
    'session.restricted': 'Session details are restricted for your plan',
    
    // Custom Code
    'customCode.title': 'Custom Code',
    'customCode.subtitle': 'Add custom CSS and JavaScript',
    'customCode.css': 'Custom CSS',
    'customCode.cssDesc': 'Add custom styles to your portal',
    'customCode.js': 'Custom JavaScript',
    'customCode.jsDesc': 'Add custom scripts to your portal',
    'customCode.warning': 'Custom code can affect portal functionality. Use with caution.',
    'customCode.disabled': 'Custom code is disabled for your plan',
    'customCode.platformOnly': 'Only available for Platform Owners',
  },
  
  es: {
    // Common
    'common.loading': 'Cargando...',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.close': 'Cerrar',
    'common.search': 'Buscar',
    'common.filter': 'Filtrar',
    'common.reset': 'Restablecer',
    'common.back': 'Atrás',
    'common.next': 'Siguiente',
    'common.submit': 'Enviar',
    'common.confirm': 'Confirmar',
    'common.yes': 'Sí',
    'common.no': 'No',
    
    // Navigation
    'nav.dashboard': 'Panel',
    'nav.agencies': 'Agencias',
    'nav.users': 'Usuarios',
    'nav.activity': 'Actividad',
    'nav.settings': 'Configuración',
    'nav.signOut': 'Cerrar Sesión',
    'nav.quickSearch': 'Búsqueda Rápida',
    
    // Settings
    'settings.title': 'Configuración',
    'settings.profile': 'Perfil',
    'settings.appearance': 'Apariencia',
    'settings.preferences': 'Preferencias',
    'settings.security': 'Seguridad',
    'settings.advanced': 'Avanzado',
    'settings.branding': 'Marca',
    
    // Appearance
    'appearance.title': 'Apariencia',
    'appearance.subtitle': 'Personaliza cómo se ve FundElite para ti',
    'appearance.accentColor': 'Color de Acento',
    'appearance.accentColorDesc': 'Elige tu color de acento preferido',
    'appearance.mode': 'Modo de Apariencia',
    'appearance.modeDesc': 'Selecciona modo claro, oscuro o del sistema',
    'appearance.system': 'Sistema',
    'appearance.light': 'Claro',
    'appearance.dark': 'Oscuro',
    'appearance.language': 'Idioma',
    'appearance.languageDesc': 'Elige tu idioma preferido',
    'appearance.resetDefaults': 'Restablecer Predeterminados',
    'appearance.resetDesc': 'Restaurar toda la configuración de apariencia',
    
    // Accent presets
    'accent.fundelite_emerald': 'Esmeralda FundElite',
    'accent.deep_teal': 'Verde Azulado Profundo',
    'accent.mint_teal': 'Verde Menta',
    'accent.dark_emerald': 'Esmeralda Oscuro',
    'accent.soft_green': 'Verde Suave',
    'accent.slate_premium': 'Pizarra Premium',
    
    // Support Mode
    'support.mode': 'Modo de Soporte',
    'support.viewingAs': 'Viendo como',
    'support.agency': 'Agencia',
    'support.returnToPlatform': 'Volver al Propietario de Plataforma',
    'support.readOnly': 'Solo Lectura',
    'support.interactive': 'Interactivo',
    
    // Context
    'context.platformOwner': 'Propietario de Plataforma',
    'context.agencyAdmin': 'Admin de Agencia',
    'context.client': 'Cliente',
    'context.selfServe': 'Autoservicio',
    'context.managed': 'Administrado',
    
    // Session Activity
    'session.title': 'Actividad del Equipo',
    'session.subtitle': 'Monitorea la actividad de sesiones del equipo',
    'session.userName': 'Nombre de Usuario',
    'session.email': 'Correo Electrónico',
    'session.ipAddress': 'Dirección IP',
    'session.loginTime': 'Hora de Inicio',
    'session.logoutTime': 'Hora de Cierre',
    'session.status': 'Estado',
    'session.active': 'Activo',
    'session.ended': 'Terminado',
    'session.noData': 'No hay datos de sesión disponibles',
    'session.restricted': 'Los detalles de sesión están restringidos para tu plan',
    
    // Custom Code
    'customCode.title': 'Código Personalizado',
    'customCode.subtitle': 'Agrega CSS y JavaScript personalizados',
    'customCode.css': 'CSS Personalizado',
    'customCode.cssDesc': 'Agrega estilos personalizados a tu portal',
    'customCode.js': 'JavaScript Personalizado',
    'customCode.jsDesc': 'Agrega scripts personalizados a tu portal',
    'customCode.warning': 'El código personalizado puede afectar la funcionalidad del portal. Usa con precaución.',
    'customCode.disabled': 'El código personalizado está deshabilitado para tu plan',
    'customCode.platformOnly': 'Solo disponible para Propietarios de Plataforma',
  },
};

/**
 * Get translated string with fallback to English
 */
export const t = (key, language = 'en') => {
  const langStrings = translations[language] || translations.en;
  return langStrings[key] || translations.en[key] || key;
};

/**
 * Translation hook for React components
 */
export const useTranslation = (language = 'en') => {
  const translate = (key, params = {}) => {
    let text = t(key, language);
    
    // Simple parameter replacement: {{param}}
    Object.entries(params).forEach(([k, v]) => {
      text = text.replace(new RegExp(`{{${k}}}`, 'g'), v);
    });
    
    return text;
  };
  
  return { t: translate, language };
};

export default { translations, t, useTranslation };
